// src/services/staffService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { formatToMMDDYYYY, parseMMDDYYYY } from '../utils/dateUtils';

/**
 * Get current date in MM-DD-YYYY format (Pacific timezone)
 * ✅ FIXED: Now returns MM-DD-YYYY instead of YY-MM-DD
 */
const getTodayFormatted = () => {
  const now = new Date();
  // Convert to Pacific timezone
  const pacificTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  return formatToMMDDYYYY(pacificTime); // MM-DD-YYYY format
};

/**
 * Add new staff member
 */
export const addStaffMember = async (staffData) => {
  try {
    const docRef = await addDoc(collection(db, 'staff_members'), {
      ...staffData,
      active: true,
      createdDate: Timestamp.now()
    });
    return { id: docRef.id, ...staffData };
  } catch (error) {
    console.error('Error adding staff member:', error);
    throw error;
  }
};

/**
 * Get all active staff members
 */
export const getAllStaff = async () => {
  try {
    const staffRef = collection(db, 'staff_members');
    const q = query(staffRef, where('active', '==', true));
    const snapshot = await getDocs(q);
    
    return snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .sort((a, b) => {
        const nameA = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
        const nameB = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  } catch (error) {
    console.error('Error fetching staff:', error);
    throw error;
  }
};

/**
 * Get ALL staff including temporarily paused (for admin views and reports)
 * Excludes only permanently deactivated (active: false AND temporaryDeactivation: false/undefined)
 */
export const getAllStaffIncludingPaused = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'staff_members'));
    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(s => {
        // Include active staff
        if (s.active !== false) return true;
        // Include temporarily paused staff
        if (s.temporaryDeactivation === true) return true;
        // Exclude permanently deactivated
        return false;
      })
      .sort((a, b) => {
        const nameA = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
        const nameB = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  } catch (error) {
    console.error('Error fetching staff:', error);
    throw error;
  }
};

/**
 * Get staff member by PIN
 */
export const getStaffByPin = async (pin) => {
  try {
    const q = query(
      collection(db, 'staff_members'),
      where('pin', '==', pin),
      where('active', '==', true)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Invalid PIN');
    }
    
    const docSnap = snapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error finding staff by PIN:', error);
    throw error;
  }
};

/**
 * Check in staff member
 * ✅ FIXED: Now stores date as MM-DD-YYYY
 * GPS coordinates are optional - pass null if not using GPS verification
 */
export const checkIn = async (staffId, staffName, location, coords = null) => {
  try {
    const today = getTodayFormatted(); // MM-DD-YYYY format
    
    console.log('🕐 Check-in for:', staffName, 'Date:', today);
    
    // Check if already checked in today
    const timeEntriesRef = collection(db, 'time_entries');
    const todayQuery = query(
      timeEntriesRef,
      where('staffId', '==', staffId),
      where('date', '==', today),
      where('status', 'in', ['checked-in', 'on-break'])
    );
    
    const existingSnapshot = await getDocs(todayQuery);
    if (!existingSnapshot.empty) {
      throw new Error('Already checked in for today');
    }
    
    // Create new time entry
    const docRef = await addDoc(timeEntriesRef, {
      staffId,
      staffName,
      checkInTime: Timestamp.now(),
      checkInLocation: location,
      checkInCoords: coords,
      checkOutTime: null,
      checkOutLocation: null,
      checkOutCoords: null,
      breaks: [],
      totalHours: 0,
      paidHours: 0,
      status: 'checked-in',
      date: today // ✅ MM-DD-YYYY format
    });
    
    console.log('✅ Checked in successfully. Entry ID:', docRef.id);
    return { id: docRef.id, date: today };
  } catch (error) {
    console.error('Error checking in:', error);
    throw error;
  }
};

/**
 * Check out staff member
 * GPS coordinates are optional - pass null if not using GPS verification
 */
export const checkOut = async (entryId, location, coords = null) => {
  try {
    const entryRef = doc(db, 'time_entries', entryId);
    const entryDoc = await getDoc(entryRef);
    
    if (!entryDoc.exists()) {
      throw new Error('Time entry not found');
    }
    
    const entry = entryDoc.data();
    const checkOutTime = Timestamp.now();
    
    // Calculate total hours
    const hoursWorked = (checkOutTime.toMillis() - entry.checkInTime.toMillis()) / (1000 * 60 * 60);
    
    // Calculate paid hours (subtract unpaid breaks)
    const unpaidBreakMinutes = entry.breaks
      .filter(b => b.type === 'unpaid-meal')
      .reduce((sum, b) => sum + b.duration, 0);
    
    const paidHours = hoursWorked - (unpaidBreakMinutes / 60);
    
    await updateDoc(entryRef, {
      checkOutTime,
      checkOutLocation: location,
      checkOutCoords: coords,
      totalHours: parseFloat(hoursWorked.toFixed(2)),
      paidHours: parseFloat(paidHours.toFixed(2)),
      status: 'checked-out'
    });
    
    console.log('✅ Checked out successfully. Hours:', hoursWorked.toFixed(2));
    return { totalHours: hoursWorked, paidHours };
  } catch (error) {
    console.error('Error checking out:', error);
    throw error;
  }
};

/**
 * Start break
 */
export const startBreak = async (entryId, breakType) => {
  try {
    const entryRef = doc(db, 'time_entries', entryId);
    const entryDoc = await getDoc(entryRef);
    
    if (!entryDoc.exists()) {
      throw new Error('Time entry not found');
    }
    
    const entry = entryDoc.data();
    
    // Add new break to breaks array
    const newBreak = {
      type: breakType,
      startTime: Timestamp.now(),
      endTime: null,
      duration: 0
    };
    
    await updateDoc(entryRef, {
      status: 'on-break',
      breaks: [...(entry.breaks || []), newBreak]
    });
    
    console.log('☕ Break started:', breakType);
  } catch (error) {
    console.error('Error starting break:', error);
    throw error;
  }
};

/**
 * End break
 */
export const endBreak = async (entryId) => {
  try {
    const entryRef = doc(db, 'time_entries', entryId);
    const entryDoc = await getDoc(entryRef);
    
    if (!entryDoc.exists()) {
      throw new Error('Time entry not found');
    }
    
    const entry = entryDoc.data();
    const endTime = Timestamp.now();
    
    // Find the active break (last one without endTime)
    const breaks = [...(entry.breaks || [])];
    const activeBreakIndex = breaks.findIndex(b => !b.endTime);
    
    if (activeBreakIndex === -1) {
      throw new Error('No active break found');
    }
    
    // Calculate duration in minutes
    const startTime = breaks[activeBreakIndex].startTime.toDate();
    const duration = Math.round((endTime.toDate() - startTime) / (1000 * 60));
    
    // Update the break
    breaks[activeBreakIndex] = {
      ...breaks[activeBreakIndex],
      endTime,
      duration
    };
    
    await updateDoc(entryRef, {
      status: 'checked-in',
      breaks
    });
    
    console.log('✅ Break ended. Duration:', duration, 'minutes');
  } catch (error) {
    console.error('Error ending break:', error);
    throw error;
  }
};

/**
 * Get current active entry for staff member
 * ✅ FIXED: Uses MM-DD-YYYY format
 */
export const getCurrentEntry = async (staffId) => {
  try {
    const today = getTodayFormatted(); // MM-DD-YYYY format
    
    console.log('🔍 Looking for entry on:', today, 'for staff:', staffId);
    
    const q = query(
      collection(db, 'time_entries'),
      where('staffId', '==', staffId),
      where('date', '==', today),
      where('status', 'in', ['checked-in', 'on-break'])
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log('No active entry found for today');
      return null;
    }
    
    const docSnap = snapshot.docs[0];
    console.log('✅ Found active entry:', docSnap.id);
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error getting current entry:', error);
    throw error;
  }
};

/**
 * Get time entries for date range (admin)
 * ✅ FIXED: Now properly handles MM-DD-YYYY dates
 * @param {string} startDate - Start date in MM-DD-YYYY format
 * @param {string} endDate - End date in MM-DD-YYYY format
 */
export const getTimeEntries = async (startDate, endDate) => {
  try {
    console.log('📅 Fetching time entries from', startDate, 'to', endDate);
    
    // Parse dates for comparison
    const start = parseMMDDYYYY(startDate);
    const end = parseMMDDYYYY(endDate);
    
    if (!start || !end) {
      throw new Error('Invalid date format. Expected MM-DD-YYYY');
    }
    
    // Get ALL entries and filter in memory
    // (Firestore string comparison on dates can be unreliable)
    const timeEntriesRef = collection(db, 'time_entries');
    const snapshot = await getDocs(timeEntriesRef);
    
    const entries = snapshot.docs
      .map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
      .filter(entry => {
        if (!entry.date) return false;
        
        const entryDate = parseMMDDYYYY(entry.date);
        if (!entryDate) {
          console.warn('⚠️ Invalid date in entry:', entry.id, entry.date);
          return false;
        }
        
        return entryDate >= start && entryDate <= end;
      })
      .sort((a, b) => {
        // Sort by date descending (most recent first)
        const dateA = parseMMDDYYYY(a.date);
        const dateB = parseMMDDYYYY(b.date);
        
        if (dateB - dateA !== 0) return dateB - dateA;
        
        // If same date, sort by check-in time
        return (b.checkInTime?.toMillis() || 0) - (a.checkInTime?.toMillis() || 0);
      });
    
    console.log(`✅ Found ${entries.length} entries between ${startDate} and ${endDate}`);
    
    // Debug: Show first few entries
    if (entries.length > 0) {
      console.log('📋 Sample entries:', entries.slice(0, 3).map(e => ({
        id: e.id,
        date: e.date,
        staff: e.staffName,
        hours: e.totalHours
      })));
    }
    
    return entries;
  } catch (error) {
    console.error('Error getting time entries:', error);
    throw error;
  }
};

/**
 * Update staff member
 */
export const updateStaffMember = async (staffId, updates) => {
  try {
    const staffRef = doc(db, 'staff_members', staffId);
    await updateDoc(staffRef, updates);
    console.log('✅ Staff member updated:', staffId);
  } catch (error) {
    console.error('Error updating staff member:', error);
    throw error;
  }
};

/**
 * Deactivate staff member
 */
export const deactivateStaff = async (staffId) => {
  try {
    await updateStaffMember(staffId, { active: false });
    console.log('✅ Staff member deactivated:', staffId);
  } catch (error) {
    console.error('Error deactivating staff:', error);
    throw error;
  }
};